/**
 * Categorize scores into structured format
 * @param {Object} scoresByTopicAndPriority - Scores organized by topic and priority
 * @param {Object} qualityItems - Quality items structure
 * @param {Array} processedItems - Processed project items
 * @param {boolean} isLegacyMode - Whether in legacy mode
 * @returns {Object} Categorized scores structure
 */
(function($) {
	function categorizeScores(scoresByTopicAndPriority, qualityItems, processedItems, isLegacyMode = false) {
		const categorizedScores = {};
		const potentialItemCounts = {};

		// Build a lookup for processedItems by safeguardid for quick access
		const processedLookup = {};
		if (processedItems) {
			processedItems.forEach(item => {
				processedLookup[item.safeguardid.S] = item;
			});
		}

		// Initialize potential item counts with the correct level format
		Object.keys(qualityItems).forEach(category => {
			if (!potentialItemCounts[category]) {
				potentialItemCounts[category] = {};
			}

			Object.keys(qualityItems[category]).forEach(topic => {
				if (!potentialItemCounts[category][topic]) {
					potentialItemCounts[category][topic] = isLegacyMode ? {
						"P1": 0,
						"P2": 0,
						"P3": 0,
						"P4": 0,
						"P5": 0
					} : {
						"Foundational (Level 1)": 0,
						"Hygiene (Level 2)": 0,
						"Governed (Level 3)": 0,
						"Controlled (Level 4)": 0,
						"Monitored (Level 5)": 0
					};
				}
			});
		});

		// Iterate again to create the categorized scores structure
		Object.keys(qualityItems).forEach(category => {
			if (!categorizedScores[category]) {
				categorizedScores[category] = {};
			}

			Object.keys(qualityItems[category]).forEach(topic => {
				if (!categorizedScores[category][topic]) {
					categorizedScores[category][topic] = {};
				}

				// Track NA, unfilled, and total items per priority
				const naItemsByPriority = {};
				const unfilledItemsByPriority = {};
				const filledItemsByPriority = {};
				const allItemsByPriority = {};

				// Build lists for each priority in this topic
				qualityItems[category][topic].forEach(item => {
					const safeguardId = item.safeguardid.S;
					const priorityTag = WebAssessmentCharts.normalizePriorityTag(item.safeguardprioritytag.S, isLegacyMode);
					
					if (!allItemsByPriority[priorityTag]) allItemsByPriority[priorityTag] = [];
					allItemsByPriority[priorityTag].push(safeguardId);

					// Check if this safeguardId is in processedLookup
					const processed = processedLookup[safeguardId];
					if (processed) {
						// If NA
						if (parseInt(processed.policystatus.N, 10) === 1) {
							if (!naItemsByPriority[priorityTag]) naItemsByPriority[priorityTag] = [];
							naItemsByPriority[priorityTag].push(safeguardId);
						} else {
							if (!filledItemsByPriority[priorityTag]) filledItemsByPriority[priorityTag] = [];
							filledItemsByPriority[priorityTag].push(safeguardId);
						}
					} else {
						if (!unfilledItemsByPriority[priorityTag]) unfilledItemsByPriority[priorityTag] = [];
						unfilledItemsByPriority[priorityTag].push(safeguardId);
					}
				});

				// Assign the scores to the topic within the correct category
				if (scoresByTopicAndPriority[topic]) {
					// Iterate over each priority within the topic
					Object.keys(scoresByTopicAndPriority[topic]).forEach(priority => {
						if (typeof scoresByTopicAndPriority[topic][priority] === 'object') {
							const normalizedPriority = WebAssessmentCharts.normalizePriorityTag(priority, isLegacyMode);
							categorizedScores[category][topic][normalizedPriority] = {
								...scoresByTopicAndPriority[topic][priority],
								potentialCount: allItemsByPriority[normalizedPriority] ? allItemsByPriority[normalizedPriority].length : 0,
								naItems: naItemsByPriority[normalizedPriority] || [],
								unfilledItems: unfilledItemsByPriority[normalizedPriority] || [],
								filledItems: filledItemsByPriority[normalizedPriority] || [],
								allItems: allItemsByPriority[normalizedPriority] || []
							};
						}
					});
					// Add overall maturity score if computed, otherwise default to 0
					categorizedScores[category][topic].overallMaturityScore = scoresByTopicAndPriority[topic].overallMaturityScore || 0;
				} else {
					// If no score exists for a topic, initialize with default structure
					categorizedScores[category][topic] = { overallMaturityScore: 0 };
					Object.keys(allItemsByPriority).forEach(priority => {
						categorizedScores[category][topic][priority] = {
							totalScore: 0,
							count: 0,
							potentialCount: allItemsByPriority[priority] ? allItemsByPriority[priority].length : 0,
							naItems: naItemsByPriority[priority] || [],
							unfilledItems: unfilledItemsByPriority[priority] || [],
							filledItems: filledItemsByPriority[priority] || [],
							allItems: allItemsByPriority[priority] || []
						};
					});
				}
			});
		});

		return categorizedScores;
	}

	// Export for use in other modules
	if (typeof module !== 'undefined' && module.exports) {
		module.exports = categorizeScores;
	} else if (typeof window !== 'undefined') {
		window.categorizeScores = categorizeScores;
	}

})(jQuery);